import { faCheck, faSpinner, faXmark } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from "react-i18next";
import { useProfileContext } from "../../../contexts/ProfileContext";
import { ApprovalGroup } from "../../../core/models/entities/Approval";
import { Role } from "../../../core/models/entities/Role";
import { ConfirmationGuard } from "../ConfirmationGuard";
import { useRejectLogoDialog } from "../Dialogs/reject-logo-dialog";
import IconButton from "../LogoButton/IconButton";

export function ApprovalVoting({
  message,
  approvalGroup,
  placeVerdict,
  rejectDelete,
  isForOrders,
  isLoading,
  fullWidth,
}: {
  message?: string;
  approvalGroup: ApprovalGroup;
  placeVerdict: (verdict: Verdict) => void;
  rejectDelete: (reason?: string) => void;
  isForOrders?: boolean;
  isLoading?: boolean;
  fullWidth?: boolean;
}) {
  const { t } = useTranslation();
  const { role } = useProfileContext();
  const rejectionDialog = useRejectLogoDialog(
    "",
    (reason) => placeVerdict({ approve: false, rejectionNote: reason }),
    (reason) => rejectDelete(reason),
    () => {},
    isForOrders
  );

  function showButtons() {
    if (
      role === Role.EndUser &&
      (approvalGroup.customer?.approver || approvalGroup.customer?.status == 0)
    ) {
      return true;
    }
    if (
      role === Role.Vendor &&
      (!approvalGroup.vendor?.approver || approvalGroup.vendor?.status === 0)
    ) {
      return true;
    }

    return false;
  }

  const show = showButtons();

  const approveButton = (
    <ConfirmationGuard
      anchor={
        <IconButton
          disabled={role === Role.Admin || isLoading}
          key={"approve"}
          variant="success"
          width={fullWidth ? "full" : undefined}
          icon={
            <FontAwesomeIcon
              icon={isLoading ? faSpinner : faCheck}
              spin={isLoading}
            />
          }
        >
          {isLoading ? t("processing") : t("compDialogsInput.approve")}
        </IconButton>
      }
      text={message ?? t("compDialogsInput.confirmApproveLogo")}
      onAccept={(e) => {
        placeVerdict({ approve: true });
      }}
    />
  );

  const rejectButton = (
    <ConfirmationGuard
      anchor={
        <IconButton
          disabled={role === Role.Admin || isLoading}
          key={"reject"}
          variant="error"
          width={fullWidth ? "full" : undefined}
          icon={<FontAwesomeIcon icon={faXmark} />}
        >
          {t("compDialogsInput.reject")}
        </IconButton>
      }
      text={t("compDialogsInput.confirmRejectItem")}
      onAccept={rejectionDialog.open}
    />
  );

  return (
    <>
      {rejectionDialog.component}
      {show && approveButton}
      {show && rejectButton}
    </>
  );
}

export interface Verdict {
  approve: boolean;
  rejectionNote?: string;
}
